import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { WaveMark } from "@/components/ui/WaveMark";

const STORAGE_KEY = "flow-onboard-done";

interface Step {
  title: string;
  body: string;
  primary: string;
  highlight?: string;
}

const STEPS: Step[] = [
  {
    title: "Hi — I'm Flow.",
    body: "I run browser automations for UnityMSK. Eligibility checks, referrals, auth submissions, claims. Work that used to eat your team's mornings. I'll show you around — this takes about two minutes.",
    primary: "Let's go",
  },
  {
    title: "This is your wall of agents.",
    body: "Every tile is a workflow running right now. Color tells you what's happening — blue is running, amber is waiting on you, red is stuck. Click any tile to go deeper.",
    primary: "Next",
    highlight: "dashboard-grid",
  },
  {
    title: "When I need you, I'll tell you.",
    body: "If a workflow hits an approval gate or gets stuck, it shows up in the Needs Attention rail. You can approve, deny, or take over without leaving the page.",
    primary: "Next",
    highlight: "attention-rail",
  },
  {
    title: "Taking over is easy.",
    body: "Hit Take Control on any paused job. You'll get a live session into the runner VM — I step aside and you finish it by hand. I'll save the recording so next time I can learn from what you did.",
    primary: "Next",
  },
  {
    title: "Build new workflows by talking to me.",
    body: "When you're ready to automate something new, open the workflow editor. You describe the work, I draft it. We iterate until it's right. You don't need to write any code.",
    primary: "Next",
  },
  {
    title: "That's the tour.",
    body: "You can reach me anytime from the chat sidebar — ask me what's running, pause a workflow, or investigate a failure. Ready?",
    primary: "Start using Flow",
  },
];

export function shouldShowOnboarding(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "1";
  } catch {
    return false;
  }
}

export function markOnboardingDone() {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // Ignore; private-mode or storage-disabled is non-fatal.
  }
}

interface Props {
  onClose: () => void;
}

export function Onboarding({ onClose }: Props) {
  const [index, setIndex] = useState(0);
  const total = STEPS.length;
  const step = STEPS[Math.min(Math.max(index, 0), total - 1)]!;

  const highlightRect = useHighlightRect(step.highlight);

  const next = () => {
    if (index + 1 >= total) {
      finish();
    } else {
      setIndex(index + 1);
    }
  };

  const back = () => setIndex((i) => Math.max(0, i - 1));

  const finish = () => {
    markOnboardingDone();
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish();
      } else if (e.key === "Enter" || e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-6"
      style={{ background: "rgba(26, 29, 31, 0.55)", backdropFilter: "blur(3px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="flow-onboarding-title"
    >
      {highlightRect && (
        <div
          className="pointer-events-none absolute rounded-md border-2 border-teal-500 transition-all"
          style={{
            top: highlightRect.top - 6,
            left: highlightRect.left - 6,
            width: highlightRect.width + 12,
            height: highlightRect.height + 12,
            boxShadow: "0 0 0 9999px rgba(26, 29, 31, 0.55)",
          }}
        />
      )}

      <div className="relative w-full max-w-[520px] rounded-lg border border-paper-line bg-paper-3 shadow-xl">
        <div className="flex items-center justify-between border-b border-paper-line px-6 py-3">
          <span className="font-mono text-xs text-ink-4">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={finish}
            className="text-xs text-ink-3 hover:text-ink-1"
          >
            Skip tour
          </button>
        </div>

        <div className="flex flex-col items-center gap-4 px-8 py-8 text-center">
          <WaveMark size={72} />
          <h2
            id="flow-onboarding-title"
            className="font-serif text-[24px] italic leading-tight text-ink-1"
          >
            {step.title}
          </h2>
          <p className="text-sm leading-relaxed text-ink-2">{step.body}</p>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-paper-line px-6 py-4">
          <button
            type="button"
            onClick={back}
            disabled={index === 0}
            className="rounded-sm px-3 py-1.5 text-sm text-ink-3 hover:text-ink-1 disabled:opacity-30"
          >
            Back
          </button>

          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={[
                  "h-1.5 rounded-pill transition-all",
                  i === index ? "w-5 bg-teal-500" : "w-1.5 bg-paper-line-strong",
                ].join(" ")}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            className="flex items-center gap-1.5 rounded-sm border border-teal-600 bg-teal-500 px-4 py-1.5 text-sm font-medium text-paper-3 hover:bg-teal-600"
          >
            {step.primary}
            <ArrowRight size={14} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Measure a [data-tour="..."] element on the page so we can render a
 * highlight ring around it. Re-measures when the target id changes or
 * the window resizes.
 */
function useHighlightRect(tourId: string | undefined) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Stabilize deps: memo on id only.
  const id = useMemo(() => tourId ?? null, [tourId]);

  useEffect(() => {
    if (!id) {
      setRect(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${id}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };

    measure();
    const ro = new ResizeObserver(measure);
    const el = document.querySelector<HTMLElement>(`[data-tour="${id}"]`);
    if (el) ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [id]);

  return rect;
}
